import React, { FC, useMemo } from "react";
import { CategoryData, Product } from "data/data";
import { PlusIcon } from "@heroicons/react/24/solid";
import { useCartDataProvider } from "context/CartProductContext";
import { useOnlineOrderSettings } from "context/OnlineOrderSettingsContext";
import { useUI } from "context/UIContext";
import NcImage from "shared/NcImage/NcImage";
import { hasModifiers } from "utils/modifierHelpers";
import toast from "react-hot-toast";
import { Transition } from "@headlessui/react";

interface CartSuggestionsProps {
  onCustomize?: (product: Product) => void;
  maxItems?: number;
}

const CartSuggestions: FC<CartSuggestionsProps> = ({
  onCustomize: onCustomizeProp,
  maxItems = 4,
}) => {
  const { Products, dispatch } = useCartDataProvider();
  const { items } = useOnlineOrderSettings();
  const { openCustomization } = useUI();
  const onCustomize = onCustomizeProp || openCustomization;

  const suggestions = useMemo(() => {
    if (!items || !Products.length) return [];

    const categories = items as CategoryData[];
    const cartItemIds = new Set(Products.map((p: Product) => p.id));

    // Find the category of the last added item
    const lastItem = Products[Products.length - 1] as Product;
    let targetCategory = categories.find((cat) =>
      cat.items?.some((item: Product) => item.id === lastItem?.id)
    );

    // If not found, use the last category (often drinks/desserts)
    if (!targetCategory) {
      targetCategory = categories[categories.length - 1];
    }

    if (!targetCategory) return [];

    // Get items from that category not already in cart
    const available = targetCategory.items
      ?.filter(
        (item: Product) =>
          !cartItemIds.has(item.id) &&
          (item.isAvailable === 2 || item.isAvailable === 3)
      )
      .slice(0, maxItems);

    return available || [];
  }, [items, Products, maxItems]);

  const handleQuickAdd = (product: Product) => {
    dispatch({ type: "QUICK_ADD", payload: product });
    toast.custom(
      (t) => (
        <Transition
          appear
          show={t.visible}
          className="p-3 max-w-sm w-full bg-white dark:bg-slate-800 shadow-lg rounded-xl pointer-events-auto ring-1 ring-black/5 dark:ring-white/10 text-slate-900 dark:text-slate-100"
          enter="transition-all duration-200"
          enterFrom="opacity-0 translate-y-4"
          enterTo="opacity-100 translate-y-0"
          leave="transition-all duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-4"
        >
          <p className="text-sm font-semibold">Added {product.name}</p>
        </Transition>
      ),
      { position: "bottom-center", id: `suggest-${product.id}`, duration: 1200 }
    );
  };

  if (suggestions.length === 0) return null;

  return (
    <div className="px-4 py-4 border-t border-slate-100 dark:border-slate-700">
      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
        You might also like
      </h4>
      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
        {suggestions.map((item: Product) => {
          const isSimple = !hasModifiers(item);
          return (
            <div
              key={item.id}
              className="flex-shrink-0 w-[140px] bg-slate-50 dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-100 dark:border-slate-700"
            >
              <div className="relative h-20 bg-stone-100 dark:bg-slate-700">
                <NcImage
                  containerClassName="w-full h-full"
                  src={item.imageUrl || ""}
                  className="object-cover w-full h-full"
                />
              </div>
              <div className="p-2">
                <p className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {item.name}
                </p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs font-bold text-primary-600 dark:text-primary-400">
                    ${item.onlinePrice?.toFixed(2)}
                  </span>
                  <button
                    onClick={() =>
                      isSimple
                        ? handleQuickAdd(item)
                        : onCustomize?.(item)
                    }
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 dark:bg-primary-400 text-white active:scale-90 transition-transform"
                    aria-label={`Add ${item.name}`}
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CartSuggestions;
